<template>
	<div>
		<v-alert v-if="!hasRequiredParams" :value="true" type="error" outline>
			This form submission link is invalid.
		</v-alert>

		<div v-else>
			<v-alert v-if="loadError" :value="true" type="error" outline class="text-xs-left mb-3">
				{{ loadError }}
			</v-alert>

			<div class="submission-context">
				<v-breadcrumbs class="pa-0 mb-2">
					<v-breadcrumbs-item @click="goHome">Home</v-breadcrumbs-item>
					<v-breadcrumbs-item v-if="showSubjectProfileLink" @click="goToSubjectProfile">{{ subjectDisplayName }}</v-breadcrumbs-item>
					<v-breadcrumbs-item v-else disabled>{{ subjectDisplayName || 'Employee' }}</v-breadcrumbs-item>
					<v-breadcrumbs-item disabled>Forms</v-breadcrumbs-item>
					<template #divider>
						<v-icon>chevron_right</v-icon>
					</template>
				</v-breadcrumbs>

				<v-layout align-center>
					<v-flex class="text-xs-left">
						<div v-if="hasSubmissionMetadata" class="body-1 text--secondary submission-meta">
							<span v-if="showSubjectContext">For {{ subjectDisplayName }}</span>
							<span v-if="showSubjectContext && respondentDisplayName"> &middot; </span>
							<span v-if="respondentDisplayName">Completed by {{ respondentDisplayName }}</span>
							<span v-if="(showSubjectContext || respondentDisplayName) && submittedDateUtc"> &middot; </span>
							<span v-if="submittedDateUtc">Submitted <AppDate :date="submittedDateUtc" /></span>
						</div>
					</v-flex>
					<v-flex v-if="isLoadingSubmission" shrink>
						<v-progress-circular indeterminate color="primary" size="22" width="2" />
					</v-flex>
				</v-layout>
			</div>

			<embedded-forms-app-frame
				title="Form Submission"
				:path="iframePath"
				@authLoginStarted="onEmbeddedFormsAuthStarted"
				@authLoginFailed="onEmbeddedFormsAuthFailed"
			/>
		</div>
	</div>
</template>

<script>
import Vue from 'vue';
import EmbeddedFormsAppFrame from '@/components/forms/EmbeddedFormsAppFrame.vue';

export default {
	name: 'PageFormSubmission',
	components: {
		EmbeddedFormsAppFrame
	},
	props: {
		tenantId: {
			type: String,
			required: true
		},
		submissionId: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			isLoadingSubmission: false,
			submission: null,
			loadError: null
		};
	},
	computed: {
		hasRequiredParams() {
			return !!this.tenantId && !!this.submissionId;
		},
		iframePath() {
			return `/internal/forms/${this.tenantId}/${this.submissionId}/view`;
		},
		subjectDisplayName() {
			return this.submission && this.submission.subjectDisplayName ? this.submission.subjectDisplayName : '';
		},
		respondentDisplayName() {
			return this.submission && this.submission.respondentDisplayName ? this.submission.respondentDisplayName : '';
		},
		isSelfSubmission() {
			if (!this.submission) return false;

			if (this.submission.subjectEmployeeId && this.submission.respondentEmployeeId) {
				return this.submission.subjectEmployeeId.toLowerCase() === this.submission.respondentEmployeeId.toLowerCase();
			}

			return this.normaliseName(this.subjectDisplayName) !== '' && this.normaliseName(this.subjectDisplayName) === this.normaliseName(this.respondentDisplayName);
		},
		showSubjectContext() {
			return !!this.subjectDisplayName && !this.isSelfSubmission;
		},
		hasSubmissionMetadata() {
			return this.showSubjectContext || !!this.respondentDisplayName || !!this.submittedDateUtc;
		},
		submittedDateUtc() {
			return this.submission ? this.submission.submittedDateUtc : null;
		},
		showSubjectProfileLink() {
			return !!(this.submission && this.submission.canViewSubjectEmployeeProfile && this.submission.subjectEmployeeId);
		}
	},
	created() {
		this.$store.dispatch('setPageTitle', 'Form Submission');
		this.loadSubmissionContext();
	},
	watch: {
		submissionId() {
			this.loadSubmissionContext();
		},
		tenantId() {
			this.loadSubmissionContext();
		}
	},
	methods: {
		async loadSubmissionContext() {
			if (!this.hasRequiredParams) return;

			this.isLoadingSubmission = true;
			this.loadError = null;

			try {
				const response = await Vue.axios.get(`/api/${this.tenantId}/form-submissions/${this.submissionId}`);
				this.submission = response.data;
			} catch (error) {
				this.loadError = 'This form submission could not be loaded.';
			} finally {
				this.isLoadingSubmission = false;
			}
		},
		goHome() {
			this.$router.push('/');
		},
		goToSubjectProfile() {
			if (!this.showSubjectProfileLink) return;
			this.$router.push({ name: 'employee', params: { id: this.submission.subjectEmployeeId } });
		},
		normaliseName(value) {
			return (value || '').trim().toLowerCase();
		},
		onEmbeddedFormsAuthStarted() {
			this.$store.dispatch('setPageTitle', 'Signing in to forms...');
		},
		onEmbeddedFormsAuthFailed() {
			this.$store.dispatch('setPageTitle', 'Form Submission');
		}
	}
};
</script>

<style scoped>
.submission-context {
	margin-bottom: 10px;
	padding: 0 4px 10px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.submission-meta {
	line-height: 20px;
}
</style>
